/**
 * ReviewerControlBar — the bottom disposition bar for the reviewer INLINE view.
 *
 * Deliberately mirrors ReviewerPanel's List-mode disposition block (cohort
 * agreement header + Overall assessment + Right/Wrong/Borderline + note +
 * Save/Next) so the two modes read identically, and adds the Configure gear
 * (mode switch) on the right of the "Overall assessment" row. The disposition
 * write path mirrors ReviewerPanel (submitDisposition + nextUnreviewedStudy).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  submitDisposition,
  useReviewCohort,
  studyKeys,
  type DispositionVerdict,
} from "../../hooks/useStudyQueries";
import { useReviewerAgreementStats } from "../../hooks/useReviewerFindingVerdicts";
import { readerCaseId } from "../../utils/readerCaseId";
import { nextUnreviewedStudy } from "../../utils/reviewerNextCase";
import { OVERALL_VERDICTS } from "./reviewerVerdictOptions";
import { ReviewerSettingsMenu } from "./ReviewerSettingsMenu";
import type { ReviewerDisplayMode } from "../../atoms/reviewerPrefsAtoms";

interface Props {
  mode: ReviewerDisplayMode;
  onChangeMode: (mode: ReviewerDisplayMode) => void;
  cohortId: string;
  studyUid: string;
  canEditTemplates: boolean;
}

export function ReviewerControlBar({
  mode,
  onChangeMode,
  cohortId,
  studyUid,
  canEditTemplates,
}: Props) {
  const { userId, token } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: cohort } = useReviewCohort(token, cohortId ?? null, userId ?? null);
  const { data: agreementStats } = useReviewerAgreementStats(
    token,
    userId ?? null,
    cohortId ?? null,
  );
  const cohortTotal = cohort?.studies.length ?? null;
  const disposition = cohort?.studies.find((s) => s.study_uid === studyUid)?.disposition ?? null;

  const [overall, setOverall] = useState<DispositionVerdict | null>(null);
  const [note, setNote] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Overall verdict + note are seeded ONCE from the existing disposition, then
  // owned by the user — mirrors ReviewerPanel (List mode). Without this, an
  // already-reviewed case opens blank (looks unreviewed) AND a re-save sends
  // comment:null, silently wiping the previously-saved note (submitDisposition
  // is an idempotent-overwrite PutItem). `seededRef` keeps the seed idempotent;
  // `markOwned()` on the edit handlers stops a late-arriving cohort fetch from
  // clobbering an assessment the reviewer started before it loaded.
  const seededRef = useRef(false);
  const markOwned = () => {
    seededRef.current = true;
  };
  useEffect(() => {
    if (seededRef.current || !disposition) return;
    setOverall(disposition.verdict);
    setNote(disposition.comment ?? "");
    setJustSaved(true);
    seededRef.current = true;
  }, [disposition]);

  const nextUnreviewed = useMemo(
    () => nextUnreviewedStudy(cohort?.studies ?? [], studyUid),
    [cohort, studyUid],
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      submitDisposition(token!, {
        cohortId,
        studyUid,
        verdict: overall!,
        comment: note.trim() ? note.trim() : null,
      }),
    onSuccess: () => {
      setJustSaved(true);
      setSaveError(null);
      queryClient.invalidateQueries({ queryKey: studyKeys.reviewCohort(cohortId, userId ?? "") });
    },
    onError: (err) => {
      // Idempotent PutItem, so a retry is safe — but never swallow the failure.
      const detail = axios.isAxiosError(err)
        ? (err.response?.data?.detail ?? err.message)
        : err instanceof Error
          ? err.message
          : "Failed to save assessment";
      console.error("[reviewer] disposition save failed", err);
      setSaveError(detail);
    },
  });

  const goToNextCase = () => {
    if (!nextUnreviewed) return;
    navigate(`/case-viewer/${readerCaseId(nextUnreviewed)}?cohort=${encodeURIComponent(cohortId)}`);
  };

  return (
    <div className="shrink-0 space-y-2 border-t border-border bg-surface p-3">
      <div className="flex items-center justify-between border-b border-border pb-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-fg-dim">
          Cohort · agreement
        </span>
        <span className="flex items-center gap-3 text-[11px] text-fg-muted">
          <span title="Agreed / (agreed + disagreed) across this cohort — your read, no reference standard">
            Agreement{" "}
            <strong className="font-semibold text-fg">
              {agreementStats?.agreement_rate != null
                ? `${Math.round(agreementStats.agreement_rate * 100)}%`
                : "—"}
            </strong>
          </span>
          <span>
            Reviewed{" "}
            <strong className="font-semibold text-fg">
              {agreementStats?.cases_reviewed ?? 0}
              {cohortTotal != null ? `/${cohortTotal}` : ""}
            </strong>
          </span>
        </span>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-widest text-fg-muted">
          Overall assessment
        </span>
        <ReviewerSettingsMenu
          mode={mode}
          onChangeMode={onChangeMode}
          canEditTemplates={canEditTemplates}
        />
      </div>

      <div className="flex gap-1.5">
        {OVERALL_VERDICTS.map((v) => {
          const selected = overall === v.value;
          return (
            <button
              key={v.value}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                markOwned();
                setOverall((cur) => (cur === v.value ? null : v.value));
                setJustSaved(false);
                setSaveError(null);
              }}
              className={`h-8 flex-1 rounded border text-xs font-semibold transition-colors ${
                selected ? v.active : "border-border text-fg-muted hover:bg-surface-2"
              }`}
            >
              {v.label}
            </button>
          );
        })}
      </div>

      <textarea
        value={note}
        onChange={(e) => {
          markOwned();
          setNote(e.target.value);
          setJustSaved(false);
          setSaveError(null);
        }}
        placeholder="Note (e.g. AI missed a crucial finding)…"
        rows={2}
        className="w-full resize-none rounded border border-border bg-surface px-2 py-1.5 text-xs text-fg placeholder:text-fg-dim focus:border-primary/60 focus:outline-none"
      />

      <div className="flex items-center">
        <button
          type="button"
          onClick={justSaved ? () => setJustSaved(false) : () => saveMutation.mutate()}
          disabled={!justSaved && (!overall || saveMutation.isPending)}
          className="h-8 flex-1 rounded bg-primary text-xs font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {saveMutation.isPending ? "Saving…" : justSaved ? "Saved ✓" : "Save assessment"}
        </button>
        <button
          type="button"
          onClick={goToNextCase}
          disabled={!nextUnreviewed}
          aria-hidden={!justSaved}
          tabIndex={justSaved ? 0 : -1}
          title={
            justSaved && !nextUnreviewed ? "No more unreviewed cases in this cohort" : undefined
          }
          className={`h-8 shrink-0 overflow-hidden whitespace-nowrap rounded border border-primary text-xs font-semibold text-primary transition-all duration-200 ease-out hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-40 ${
            justSaved
              ? "ml-1.5 w-1/2 px-2 opacity-100"
              : "ml-0 w-0 border-0 px-0 opacity-0 pointer-events-none"
          }`}
        >
          {nextUnreviewed ? "Next case →" : "All reviewed"}
        </button>
      </div>

      {saveError && (
        <p data-testid="disposition-error" className="text-[11px] font-semibold text-warn">
          Couldn’t save — {saveError}
        </p>
      )}
    </div>
  );
}

export default ReviewerControlBar;
